import { useEffect, useState, useRef } from 'react';
import { getProfileInfo } from 'services/utils';
import { getLevelNodeEnrollment } from 'services/api';

interface TrainingCompletionWatcherProps {
    props: any;
    setTrainingNotification: (state: any) => void;
}

interface ContentNavigationData {
    idEnrollment: string | null;
    idContentItem: string | null;
    response: any;
}

const useTrainingCompletionWatcher = ({ props, setTrainingNotification }: TrainingCompletionWatcherProps) => {
    const [contentItem, setContentItem] = useState<string | null>(null);
    const [currentTrainingNode, setCurrentTrainingNode] = useState<any>(null);
    const [contentNavigationData, setContentNavigationData] = useState<ContentNavigationData>({
        idEnrollment: null,
        idContentItem: null,
        response: null,
    });
 
    const fallbackProcessingRef = useRef<boolean>(false); 
    const idUser = getProfileInfo()?.idUser;
 
    const handleTrainingCompletionFallback = async (nodeData: any) => { 
        if (fallbackProcessingRef.current) {
            return;
        }
        fallbackProcessingRef.current = true;
        try {
            const idEnrollment = nodeData?.nodeEnrollment?.enrollment?.idEnrollment;
            if (!idEnrollment) {
                return;
            }
            const nodeCode = nodeData?.nodeCode || currentTrainingNode?.nodeCode;
            let updatedResponse;
            if (nodeCode) {
                updatedResponse = await getLevelNodeEnrollment(idUser, idEnrollment, nodeCode);
            } else {
                updatedResponse = await getLevelNodeEnrollment(idUser, idEnrollment, null);
            }
            if (updatedResponse?.data?.node) {
                const updatedNodeData = updatedResponse.data.node;
                const hasUpdatedImpactEvaluation = updatedNodeData.hasStudentImpactEvaluation === 1;
                if (!hasUpdatedImpactEvaluation) {
                    return;
                }
                const previousCompleted = currentTrainingNode?.hasEnrollmentCompleted;
                const currentCompleted = updatedNodeData?.hasEnrollmentCompleted;
                const statusChanged = previousCompleted === false && currentCompleted === true;
                if (statusChanged) {
                    const daysToComplete = Number(
                        (updatedNodeData?.studentImpactEvaluationParams || []).find(
                            (param: any) => param.parameter === 'availableUntilDays',
                        )?.parameterValue ?? 0,
                    );

                    const availableAfterDays = Number(
                        (nodeData?.studentImpactEvaluationParams || []).find(
                            (param: any) => param.parameter === 'availableAfterDays',
                        )?.parameterValue ?? 0
                    );

                    setTrainingNotification({
                        impactEvaluationStatus: true,
                        trainingTitle: updatedNodeData?.contentItem?.name || 'Treinamento',
                        daysToComplete,
                        availableAfterDays,
                        customData: {
                            nodeData: updatedNodeData,
                            completedTraining: {
                                title: updatedNodeData?.contentItem?.name,
                                idEnrollment: updatedNodeData?.nodeEnrollment?.idEnrollment,
                                status: updatedNodeData?.nodeEnrollment?.status,
                            },
                        },
                    });
                }
                setCurrentTrainingNode(updatedNodeData);
            }
        } catch (error) {
            console.error('[TrainingCompletionWatcher] Erro no fallback:', error);
        } finally {
            fallbackProcessingRef.current = false;
        }
    };
 
    const verifyCompleteTraining = (nodeData: any) => {
        const enrollmentStatus = nodeData?.nodeEnrollment?.status;
        const hasStudentImpactEvaluation = nodeData?.hasStudentImpactEvaluation === 1;
        if (
            currentTrainingNode?.nodeEnrollment?.status !== 'COMPLETED' &&
            currentTrainingNode?.nodeEnrollment?.status !== 'PASSED'
        ) {
            if ((enrollmentStatus === 'COMPLETED' || enrollmentStatus === 'PASSED') && hasStudentImpactEvaluation) {
                const daysToComplete = Number(
                    (nodeData?.studentImpactEvaluationParams || []).find(
                        (param: any) => param.parameter === 'availableUntilDays',
                    )?.parameterValue ?? 0,
                );

                const availableAfterDays = Number(
                        (nodeData?.studentImpactEvaluationParams || []).find(
                            (param: any) => param.parameter === 'availableAfterDays',
                        )?.parameterValue ?? 0
                    );
                setTrainingNotification({
                    impactEvaluationStatus: true,
                    trainingTitle: nodeData?.contentItem?.name || 'Treinamento',
                    daysToComplete,
                    availableAfterDays,
                    customData: {
                        nodeData,
                        completedTraining: {
                            title: nodeData?.contentItem?.name,
                            idEnrollment: nodeData?.nodeEnrollment?.idEnrollment,
                            status: nodeData?.nodeEnrollment?.status,
                        },
                    },
                });
            } else if (hasStudentImpactEvaluation) {
                handleTrainingCompletionFallback(nodeData);
            }
        }
        setCurrentTrainingNode(null);
        setContentItem(null);
    };
 
    const verifyTraining = () => {
        const { idContentItem, response } = contentNavigationData;
        if (contentItem === null || idContentItem === contentItem) {
            if (!currentTrainingNode) {
                if (response?.data?.node) {
                    setCurrentTrainingNode(response.data.node);
                    setContentItem(idContentItem);
                }
            } else {
                if (response?.data?.node) {
                    verifyCompleteTraining(response.data.node);
                }
            }
        } else {
            if (response?.data?.node) {
                setCurrentTrainingNode(response.data.node);
                setContentItem(idContentItem);
            }
        }
    };
 
    const handleContentNavigation = (contentNavigation: any, contentKey: string, dataKey: string) => {
        if (contentNavigation && contentNavigation[contentKey]) {
            setTimeout(() => {
                const { idEnrollment } = contentNavigation[dataKey];
                const idContentItem =
                    contentNavigation[contentKey].idContentItem || contentNavigation[contentKey].idEvaluation;
                setContentNavigationData((prev) => ({ ...prev, idEnrollment, idContentItem }));
            }, 1000);
        }
    };

    const controlContent = (contentNavigation: any) => {
        handleContentNavigation(contentNavigation, 'content', 'contentEnrollment');
    };

    const controlScorm = (contentNavigation: any) => {
        handleContentNavigation(contentNavigation, 'objScorm', 'rootContent');
    };

    const controlEvaluation = (contentNavigation: any) => {
        handleContentNavigation(contentNavigation, 'evaluation', 'currentItem');
    };
 
    useEffect(() => {
        const contentNavigation = props.configHeaderFooter?.contentNavigation;
        controlContent(contentNavigation);
        controlScorm(contentNavigation);
        controlEvaluation(contentNavigation);
    }, [props]);
 
    useEffect(() => {
        if (contentNavigationData.response) {
            verifyTraining();
        }
    }, [contentNavigationData]);
 
    useEffect(() => {
        if (!contentNavigationData.response && contentNavigationData.idEnrollment) { 
            if (props?.apiReplication?.getLevelNodeEnrollment) {
                const response = props?.apiReplication?.getLevelNodeEnrollment;
                setContentNavigationData((prev) => ({ ...prev, response }));
            } 
        }
    }, [contentNavigationData]);

    return null;
};

export default useTrainingCompletionWatcher;
